import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tenderService } from "../../services/api";
import { formatRequestDate } from "../../utils/tenderFormatters";

/**
 * The RECORD of an archived request — what happened on it, per the record
 * design addendum, and nothing else:
 *
 *   Summary line   {n} pitches received · {n} accepted · {n} rejected
 *   One row per ACCEPTED pitch: the player as a person row (photo/initials +
 *   name + position), "pitched by {agent} · {agency}", the accepted date, an
 *   outcome pill (Deal / Signed emerald · Closed elsewhere / No outcome grey),
 *   and a "Chat" affordance when the conversation still exists — chats survive
 *   closing, the link lands in the inbox. Its absence renders nothing.
 *
 * NO chat contents, NO listing of rejected pitches beyond the count, no
 * timeline prose. Zero accepted pitches show the summary line alone.
 *
 * Data comes from the archive-record endpoint and is fetched on mount: the
 * component is only mounted once the record is actually opened (card flip /
 * expanded list row), so mounting IS the lazy load.
 *
 * `variant="card"` stacks the person row (design's flipped card);
 * `variant="row"` lays it out horizontally under the RECORD label (design's
 * open list row).
 */

// outcome -> pill copy and tone, exactly the addendum's four words. `accepted`
// is an accepted pitch that never concluded — "No outcome".
const OUTCOMES = {
  deal: { label: "Deal", good: true },
  signed: { label: "Signed", good: true },
  external_deal: { label: "Closed elsewhere", good: false },
  accepted: { label: "No outcome", good: false },
};

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const outcomePill = (outcome) => {
  const spec = OUTCOMES[outcome] || OUTCOMES.accepted;
  const tone = spec.good
    ? "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]"
    : "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]";
  return (
    <span
      className={`px-2.5 py-1 text-[11.5px] font-bold rounded-full border whitespace-nowrap flex-none ${tone}`}
    >
      {spec.label}
    </span>
  );
};

const ArchiveRecord = ({ tender, variant = "card" }) => {
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await tenderService.getArchiveRecord(
          tender.id,
          controller.signal
        );
        if (response.success) {
          setRecord(response.data);
        } else {
          setError(response.error);
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load the record");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [tender.id]);

  if (loading) {
    return (
      <div className="flex justify-center py-4" data-testid="record-loading">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="py-2 text-sm text-[#535370]">{error}</div>;
  }

  const summary = record?.summary || { received: 0, accepted: 0, rejected: 0 };
  const rows = Array.isArray(record?.rows) ? record.rows : [];
  const isRow = variant === "row";

  const openChat = (channelId) => {
    // Same deep-link contract every tender/pitch flow uses (useInbox honours
    // location.state.channelId; channelType defaults to "messaging").
    navigate("/inbox", { state: { channelId } });
  };

  return (
    <div className="flex flex-col gap-3">
      {isRow && (
        <div className="text-xs font-bold uppercase tracking-wide text-[#64748B]">
          Record
        </div>
      )}
      <div className="text-[13.5px] font-semibold text-[#475569]">
        {`${summary.received} pitches received · ${summary.accepted} accepted · ${summary.rejected} rejected`}
      </div>
      {rows.length > 0 && (
        <div className="text-[11.5px] font-bold uppercase tracking-[0.07em] text-[#047857]">
          Accepted pitches
        </div>
      )}
      {rows.map((row, index) => {
        const name = row.player?.name || "";
        return (
          <div
            // The record has no row ids of its own; index over an immutable
            // server list is stable here.
            // eslint-disable-next-line react/no-array-index-key
            key={`record-row-${index}`}
            className={`bg-[#F8FAFB] border border-[#EDF0F4] rounded-[10px] ${
              isRow
                ? "flex items-center gap-3 flex-wrap min-w-0 px-3 py-2.5"
                : "flex items-start gap-3 min-w-0 px-3.5 py-3"
            }`}
          >
            {row.player?.photo ? (
              <img
                src={row.player.photo}
                alt={name}
                referrerPolicy="no-referrer"
                className="w-9 h-9 rounded-full object-cover flex-none"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#EDF0F4] text-[#475569] text-xs font-bold flex items-center justify-center flex-none">
                {/* A STAFF pitch carries no player at all (the endpoint sends
                    name/photo/position as null) — the circle then shows a
                    neutral person glyph instead of empty initials. */}
                {name ? (
                  initialsOf(name)
                ) : (
                  <svg
                    aria-hidden="true"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4 0-8 2-8 5v1h16v-1c0-3-4-5-8-5Z" />
                  </svg>
                )}
              </div>
            )}
            <div
              className={
                isRow
                  ? "flex-1 basis-[170px] min-w-0"
                  : "flex-1 min-w-0 flex flex-col gap-0.5"
              }
            >
              {/* No player (staff pitch): the row leads with the pitched-by
                  line instead of an empty bold name. */}
              {name ? (
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[15px] font-bold whitespace-nowrap overflow-hidden text-ellipsis text-[#0B0B0B]">
                    {name}
                  </span>
                  {row.player?.position && (
                    <span className="text-[12.5px] text-[#64748B] whitespace-nowrap">
                      {row.player.position}
                    </span>
                  )}
                </div>
              ) : null}
              {/* Long agent/agency names truncate with ellipsis, never clip. */}
              <div className="text-[12.5px] text-[#64748B] whitespace-nowrap overflow-hidden text-ellipsis">
                {row.agency_name
                  ? `pitched by ${row.agent_name} · ${row.agency_name}`
                  : `pitched by ${row.agent_name}`}
              </div>
              {!isRow && (
                <div className="text-[12.5px] font-semibold text-[#047857]">
                  {`✓ Accepted ${formatRequestDate(row.accepted_at)}`}
                </div>
              )}
            </div>
            <div
              className={
                isRow
                  ? "flex items-center gap-2.5 flex-none"
                  : "flex flex-col items-end gap-1.5 flex-none"
              }
            >
              {isRow && (
                <span className="text-[12.5px] font-semibold text-[#047857] whitespace-nowrap">
                  {`✓ Accepted ${formatRequestDate(row.accepted_at)}`}
                </span>
              )}
              {outcomePill(row.outcome)}
              {/* Chats survive closing: the affordance exists exactly when the
                  conversation still does, and its absence renders nothing. */}
              {row.chat_exists && row.chat_channel_id ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openChat(row.chat_channel_id);
                  }}
                  className="text-[12.5px] font-semibold text-[#2283FF] hover:underline"
                >
                  Chat
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ArchiveRecord;
